import React, { useState } from 'react';
import Modal from 'react-modal';
import { FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { reportsAPI } from '../services/api';

Modal.setAppElement('#root');

const ReportModal = ({ isOpen, onClose, userId, userName }) => {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reportReasons = [
    { value: 'spam', label: 'Spam or inappropriate content', description: 'Unsolicited messages, advertisements, or inappropriate content' },
    { value: 'harassment', label: 'Harassment or bullying', description: 'Threatening, abusive, or harmful behavior' },
    { value: 'fake_profile', label: 'Fake profile or scam', description: 'Impersonation, fraudulent activity, or deceptive profiles' },
    { value: 'inappropriate_photos', label: 'Inappropriate photos', description: 'Nude, explicit, or inappropriate images' },
    { value: 'underage', label: 'Underage user', description: 'User appears to be under 18 years old' },
    { value: 'other', label: 'Other violation', description: 'Different type of violation not listed above' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reason) {
      toast.error('Please select a reason for reporting');
      return;
    }

    setIsSubmitting(true);

    try {
      await reportsAPI.submitReport({
        reported_user_id: userId,
        reason,
        description
      });

      toast.success('Report submitted successfully');
      onClose();
      setReason('');
      setDescription('');
    } catch (error) {
      console.error('Report error:', error);
      toast.error('Failed to submit report');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="modal-dialog modal-sm"
      overlayClassName="modal-overlay"
    >
      <div className="modal-content">
        <div className="modal-header bg-gradient-to-r from-red-50 to-orange-50 border-red-100">
          <h4 className="modal-title">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <FaExclamationTriangle className="text-red-600 text-lg" />
              </div>
              <div>
                <span className="block text-lg font-semibold text-gray-900">Report User</span>
                <span className="block text-sm text-gray-600 font-normal">@{userName}</span>
              </div>
            </div>
          </h4>
          <button
            type="button"
            className="modal-close hover:bg-red-50"
            onClick={onClose}
          >
            <FaTimes className="text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="space-y-6">
              {/* Report Reason Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Report Reason</h3>
                </div>

                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all bg-white"
                  required
                >
                  <option value="">Choose a reason for reporting...</option>
                  {reportReasons.map((reportReason) => (
                    <option key={reportReason.value} value={reportReason.value}>
                      {reportReason.label}
                    </option>
                  ))}
                </select>

                {reason && (
                  <div className="mt-2 p-3 bg-white border border-gray-200 rounded-md">
                    <p className="text-sm text-gray-600">
                      {reportReasons.find(r => r.value === reason)?.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Additional Details Section */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 002 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Additional Details</h3>
                </div>

                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Please provide specific details about this report. Include screenshots, links, or any evidence that supports your report..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-all bg-white"
                  rows="5"
                  maxLength="1000"
                />
                <div className="text-xs text-gray-500 mt-3 flex justify-between items-center">
                  <span className="flex items-center">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Be specific and provide evidence
                  </span>
                  <span className={`${description.length > 900 ? 'text-red-500' : 'text-gray-500'}`}>
                    {description.length}/1000
                  </span>
                </div>
              </div>

              {/* Warning Notice */}
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-semibold text-red-800 mb-1">
                      Important Notice
                    </h4>
                    <div className="text-sm text-red-700 space-y-1">
                      <p>• False reports may result in account suspension</p>
                      <p>• Please only report genuine violations</p>
                      <p>• All reports are reviewed by our moderation team</p>
                    </div>
                  </div>
                </div>
              </div>
          </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-danger"
                disabled={isSubmitting || !reason}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    Submit Report
                  </>
                )}
              </button>
            </div>
        </div>
        </form>
      </div>
    </Modal>
  );
};

export default ReportModal;
